import { GatsbyImage } from 'gatsby-plugin-image';
import { useContext } from 'react';
import { SwiperSlide } from 'swiper/react';

import { Container, Section } from 'components/GridV2';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { GetKOHOBtn } from 'components/rebrand/GetKOHOBtn';
import { LocaleContext } from 'components/rebrand/Layout';
import { SwiperCarousel } from 'components/rebrand/SwiperCarousel';

import { ISectionWithCardList } from 'typescriptDefs/pages/home';

import { useIsMobileSize } from 'helpers';
import { afTrackingByPage } from 'helpers/getAfTracking';

const SpendCard = ({
  card: {
    content: { description, heading, image },
    cta,
  },
  index,
}: {
  card: ISectionWithCardList['cards'][0];
  index: number;
}) => {
  const locale = useContext(LocaleContext);
  const location = 'Spend card - ' + cta?.registrationAdsetId;

  return (
    <div className="h-full rounded-12 bg-white p-32 md:rounded-16 md:pb-40 md:pt-36">
      <GatsbyImage className="mb-32 rounded-8 md:mb-40" image={image.img.asset.gatsbyImageData} alt={image.altText} />
      <div className="mb-16 md:mb-20 [&_*]:text-2xl md:[&_*]:text-3xl">
        <CustomPortableText content={heading} />
      </div>
      <div>
        <CustomPortableText content={description} />
      </div>
      {!!cta && (
        <GetKOHOBtn
          buttonLocation={location}
          queryParams={afTrackingByPage('HOME', cta?.registrationAdsetId)}
          trackEventProps={{
            category: 'www page Home',
            label: 'Spend card',
            locale,
            location,
          }}
        >
          {cta.text}
        </GetKOHOBtn>
      )}
    </div>
  );
};

const Spend = ({ cards, cta, heading }: ISectionWithCardList) => {
  const locale = useContext(LocaleContext);
  const isMobile = useIsMobileSize();

  return (
    <Section className="bg-primary-50 py-56 md:py-80">
      <Container className="px-32 xs:px-0">
        <div className="mb-40 text-primary-500 md:[&>*]:text-center md:[&>p]:mx-auto md:[&>p]:w-1/2">
          <CustomPortableText
            headingStyles={{
              h2: {
                className: 'md:mx-auto mb-24 md:mb-32 md:w-4/5 [&_strong]:text-primary-200',
                headingStyle: true,
              },
            }}
            content={heading}
          />
        </div>
        <GetKOHOBtn
          className="mx-auto mb-56 block w-full md:mb-42 md:w-fit"
          buttonLocation="Spend section"
          queryParams={afTrackingByPage('HOME', cta?.registrationAdsetId)}
          trackEventProps={{
            category: 'www page Home',
            label: 'Spend',
            locale,
            location: 'Spend section',
          }}
          buttonClassName="w-full"
        >
          {cta?.text}
        </GetKOHOBtn>
        <SwiperCarousel
          nextNavEl="home-spend-next"
          prevNavEl="home-spend-prev"
          noNavigation={!isMobile && cards.length <= 3}
          breakpoints={{
            0: { slidesPerView: 1, spaceBetween: 16 },
            768: { slidesPerView: 2, spaceBetween: 16 },
            992: { slidesPerView: 3, spaceBetween: 32 },
          }}
          slidesOffsetAfter={0}
        >
          {cards.map((card, index) => (
            <SwiperSlide className="!h-auto" key={index}>
              <SpendCard card={card} index={index} />
            </SwiperSlide>
          ))}
        </SwiperCarousel>
      </Container>
    </Section>
  );
};

export default Spend;
